import React, { useState, useEffect, useMemo, useCallback, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import ReactTooltip from 'react-tooltip';

import { Div, Row, Col, Icon, Text, Switch } from '@pinpoint/design-kit';
import { charts, styles } from '@pinpoint/onpoint-charts';

import { bcActions, modelViewActions } from 'src/store/actions';
import { ApiWrapper, Button } from 'src/components';
import { Page, Rbac } from 'src/containers';
import useModel from 'src/hooks/swr/useModel';
import useSegment from 'src/hooks/swr/useSegment';
import DefaultTheme from 'src/styles/DefaultTheme';
import { formatNumberV2 } from 'src/functions';

import CONSTANTS from 'src/store/constants';

const formatQuarter = (val) => {
  const date = new Date(val);
  const year = date.getUTCFullYear() % 100;
  const month = date.getUTCMonth();
  const quarter = Math.floor(month / 3) + 1;
  return `${year.toString().padStart(2, '0')}Q${quarter}`;
};

const INPUTS = {
  [CONSTANTS.INPUT_DISTRIBUTION.INPUTS.POLICY_PERIODS]: {
    COLOR: 'actuarialDir',
    HEADER_NAME: 'Policy Periods',
    DATASET_KEY: 'match_sum_policy_periods',
    format: (val) =>
      new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(val < 0 ? 0 : val),
    formatXAxis: (val) =>
      new Intl.NumberFormat('en-US', {
        minimumFractionDigits: val > 50 ? 1 : 2,
        maximumFractionDigits: val > 50 ? 1 : 2,
      }).format(val < 0 ? 0 : val),
  },
  [CONSTANTS.INPUT_DISTRIBUTION.INPUTS.OTHER_EXPOSURE]: {
    COLOR: 'otherExposure',
    HEADER_NAME: 'Other Exposure',
    DATASET_KEY: 'match_other_exposure',
    format: (val) =>
      new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(val < 0 ? 0 : val),
    formatXAxis: (val) =>
      new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(val < 0 ? 0 : val),
  },
  [CONSTANTS.INPUT_DISTRIBUTION.INPUTS.LOSS_COUNT]: {
    COLOR: 'actuarialFreq',
    HEADER_NAME: 'Claims Count',
    DATASET_KEY: 'match_sum_loss_counts',
    format: (val) => Math.floor(val),
    formatTooltip: (data) => {
      let value;
      if (data[0] <= 0) {
        // 0 bucket
        value = 0;
      } else if (data[0] - data[2] <= 1) {
        // display single bucket value when range isn't greater than 1
        value = formatNumberV2(Math.floor(data[0]));
      } else {
        value = `${formatNumberV2(Math.ceil(data[2]))} - ${formatNumberV2(Math.floor(data[0]))}`;
      }
      return `Claims Count: ${value}`;
    },
  },
  [CONSTANTS.INPUT_DISTRIBUTION.INPUTS.LOSS_DOLLARS]: {
    COLOR: 'actuarialSev',
    HEADER_NAME: 'Loss Dollars and Expenses',
    DATASET_KEY: 'match_sum_loss_dollars_and_expenses',
    format: (val) =>
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(val < 0 ? 0 : val),
    formatXAxis: (val) =>
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(val < 0 ? 0 : val),
  },
  [CONSTANTS.INPUT_DISTRIBUTION.INPUTS.PREMIUMS]: {
    COLOR: 'actuarialMult',
    HEADER_NAME: 'Premiums Earned',
    DATASET_KEY: 'match_sum_premiums_earned',
    format: (val) =>
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(val < 0 ? 0 : val),
    formatXAxis: (val) =>
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(val < 0 ? 0 : val),
  },
  [CONSTANTS.INPUT_DISTRIBUTION.INPUTS.EFFECTIVE_DATE]: {
    COLOR: 'brand300',
    COLOR_HEX: DefaultTheme.colors.brand300,
    HEADER_NAME: 'Effective Quarters',
    DATASET_KEY: 'match_sum_effective_date',
    SWITCH_BUCKET_DISPLAY: true,
    format: formatQuarter,
    formatXAxis: formatQuarter,
    formatTooltip: (data) => `Calendar Quarter: ${formatQuarter(data[0])}`,
  },
};

const FILTERS = {
  [CONSTANTS.INPUT_DISTRIBUTION.FILTERS.CLAIMANTS]: ({ claimants }) => claimants,
  [CONSTANTS.INPUT_DISTRIBUTION.FILTERS.NON_CLAIMANTS]: ({ non_claimants: nonClaimants }) => nonClaimants,
  [CONSTANTS.INPUT_DISTRIBUTION.FILTERS.ALL]: ({ count }) => count,
};

function InputDistributionsModels({
  history,
  app: {
    profile: { email },
    tenant,
  },
  location: { search: queryParams, pathname },
  match: {
    params: { modelId },
  },
  setModelName,
  fetchPostMatchInputDist,
  postInputDistDownloadUrl,
  resetPage,
}) {
  const [inputType, setInputType] = useState(CONSTANTS.INPUT_DISTRIBUTION.INPUTS.POLICY_PERIODS);
  const [filter, setFilter] = useState(CONSTANTS.INPUT_DISTRIBUTION.FILTERS.ALL);
  const [yAxisLog, setYAxisLog] = useState(false);
  const [xAxisLog, setXAxisLog] = useState(false);

  const inputConfig = useMemo(() => INPUTS[inputType], [inputType]);
  const handleFilter = useCallback((bin) => FILTERS[filter](bin), [filter]);

  const { data: model, error: modelError, isLoading: modelLoading, mutate: getModel } = useModel({ modelId });
  const {
    data: segment,
    error: segmentError,
    isLoading: segmentLoading,
    mutate: getSegmentData,
  } = useSegment({
    segmentId: model?.modelData?.dataPayload?.actuarial_segment_id,
    options: {
      headers: {
        'X-Fields': 'dataset{id,payload{match_column_counts}}',
      },
    },
  });

  const apiStatus = useMemo(() => {
    if (modelError || segmentError) {
      return CONSTANTS.API.FAILED;
    }
    if (modelLoading || segmentLoading || !model || !segment) {
      return CONSTANTS.API.STARTED;
    }
    return CONSTANTS.API.SUCCEEDED;
  }, [model, modelLoading, modelError, segment, segmentLoading, segmentError]);

  const inputData = useMemo(() => segment?.dataset?.payload?.match_column_counts || {}, [segment]);

  const hasData = useMemo(() => !!inputData[inputConfig.DATASET_KEY]?.length, [inputData, inputConfig]);

  const chartDataset = useMemo(() => {
    const processed =
      inputData[inputConfig.DATASET_KEY]?.map((bin) => {
        const { left, right } = bin;
        const count = handleFilter(bin);
        return inputConfig.SWITCH_BUCKET_DISPLAY
          ? [left, yAxisLog && !count ? 1 : count, right]
          : [right, yAxisLog && !count ? 1 : count, left];
      }) ?? [];
    return xAxisLog ? processed.slice(1) : processed;
  }, [inputData, inputConfig, handleFilter, yAxisLog, xAxisLog]);

  const chartColor = useMemo(
    () => ({
      hex: DefaultTheme.colors[inputConfig.COLOR] || inputConfig.COLOR,
      key: inputConfig.COLOR,
    }),
    [inputConfig]
  );

  // When the chart changes update the query param
  useEffect(() => {
    const query = new URLSearchParams(queryParams);
    const urlInputType = query.get('variant');
    if (!urlInputType) {
      query.append('variant', 'policy-periods');
      history.replace(`${pathname}?${query.toString()}`);
    } else {
      setInputType(CONSTANTS.INPUT_DISTRIBUTION.PARAMS[urlInputType]);
    }
  }, [queryParams, history, pathname, inputType]);

  useEffect(() => {
    if (model) {
      setModelName(model.name);
    }
  }, [model, setModelName]);

  /*
   * Fetch data whenever the tenant changes
   */
  useEffect(() => {
    if (tenant) {
      getModel();
    }
  }, [tenant, getModel]);

  useEffect(() => {
    const segmentId = model?.modelData?.dataPayload?.actuarial_segment_id;
    if (segmentId) {
      fetchPostMatchInputDist(segmentId, 'segment');
    }
  }, [model, fetchPostMatchInputDist]);

  useEffect(() => resetPage, [resetPage]);

  useLayoutEffect(() => {
    ReactTooltip.rebuild();
  });

  return (
    <Div h="100%" minH="100%" w="100%" minW="100%" data-debug="content">
      <ReactTooltip id="input-distributions-tooltip" className="pinpoint-tooltip" effect="solid" delayShow={500} />
      <Row m={{ x: '1.5rem', t: '1.5rem', b: '.9rem' }} data-debug="nav-row">
        <Col size="12" gutterWidth="0">
          <Div d="flex" justify="space-between">
            <Div d="flex" justify="flex-start" align="center">
              <Button
                buttonStyle="primary"
                onClick={() => {
                  history.push('/models');
                }}
              >
                <Icon name="LongLeft" color="white" m={{ r: '0.5rem' }} size="1rem" />
                BACK
              </Button>
              <Rbac roles={CONSTANTS.ROLES.slice(0, 4)} fallback={null}>
                <Button
                  buttonStyle="admin"
                  data-test="aprv-cl-btn"
                  data-tip="View requirements prior to model approval"
                >
                  <Icon name="Checklist" color="white" m={{ r: '0.5rem' }} viewBox="0 0 24 24" size="1.25rem" />
                  APPROVAL CHECKLIST
                </Button>
              </Rbac>
              <Rbac roles={CONSTANTS.ROLES.slice(0, 4)}>
                <Div data-test="export-btn-div">
                  <Button
                    buttonStyle="admin"
                    data-tip={
                      [null, undefined].includes(postInputDistDownloadUrl)
                        ? null
                        : 'Download each record and its inputs'
                    }
                    data-test="export-btn"
                    disabled={[null, undefined].includes(postInputDistDownloadUrl)}
                    onClick={() => window.open(postInputDistDownloadUrl, '_blank')}
                  >
                    <Icon name="Excel" color="white" size="1rem" viewBox="0 0 21 22" m={{ r: '.5rem' }} />
                    SAVE IN EXCEL
                  </Button>
                </Div>
              </Rbac>
            </Div>

            <Div d="flex" justify="flex-end" align="center">
              <Text textColor="navyBlue" textSize="title">
                Input Distributions
              </Text>
            </Div>
          </Div>
        </Col>
      </Row>

      <Div
        h="100%"
        w="100%"
        maxH="calc(100% - 3.75rem)"
        maxW="calc(100% - 2.75rem)"
        m={{ x: '1.5rem' }}
        bg="white"
        overflow="hidden"
        rounded="md"
        data-debug="container"
        flexDir="column"
        d="flex"
        flexWrap="nowrap"
      >
        <ApiWrapper apiStatus={apiStatus} hasData={hasData} getData={getSegmentData}>
          {hasData ? (
            <>
              <Row m={{ x: '1.5rem', t: '1.5rem', b: '.9rem' }} data-debug="chart-views-row">
                <Col gutterWidth="0" d="inline-flex" size="6" data-test="view-buttons">
                  <Button
                    buttonStyle="tab"
                    selected={inputType === CONSTANTS.INPUT_DISTRIBUTION.INPUTS.POLICY_PERIODS}
                    onClick={() => history.push(`${pathname}?variant=policy-periods`)}
                    data-tip="View distribution of policy periods"
                    data-for="input-distributions-tooltip"
                  >
                    <Icon name="CalendarEmpty" viewBox="0 0 19 15" color="white" m={{ r: '.5em' }} />
                    POLICY PERIODS
                  </Button>
                  {INPUTS[CONSTANTS.INPUT_DISTRIBUTION.INPUTS.OTHER_EXPOSURE].DATASET_KEY in inputData && (
                    <Button
                      buttonStyle="tab"
                      selected={inputType === CONSTANTS.INPUT_DISTRIBUTION.INPUTS.OTHER_EXPOSURE}
                      onClick={() => history.push(`${pathname}?variant=other-exposure`)}
                      data-tip="View distribution of other exposure"
                      data-for="input-distributions-tooltip"
                    >
                      <Icon name="DollarsClock" viewBox="0 0 19 17" color="white" m={{ r: '.5em' }} />
                      OTHER EXPOSURE
                    </Button>
                  )}
                  <Button
                    buttonStyle="tab"
                    selected={inputType === CONSTANTS.INPUT_DISTRIBUTION.INPUTS.LOSS_COUNT}
                    onClick={() => history.push(`${pathname}?variant=claims-count`)}
                    data-tip="View distribution of claims count"
                    data-for="input-distributions-tooltip"
                  >
                    <Icon name="CounterTower" viewBox="0 0 19 16" color="white" m={{ r: '.5rem' }} />
                    CLAIMS COUNT
                  </Button>
                  <Button
                    buttonStyle="tab"
                    selected={inputType === CONSTANTS.INPUT_DISTRIBUTION.INPUTS.LOSS_DOLLARS}
                    onClick={() => history.push(`${pathname}?variant=loss-dollars`)}
                    data-tip="View distribution of loss dollars and expenses"
                    data-for="input-distributions-tooltip"
                  >
                    <Icon name="DollarsLoss" viewBox="0 0 20 16" color="white" m={{ r: '.5rem' }} />
                    LOSS DOLLARS
                  </Button>
                  <Button
                    buttonStyle="tab"
                    selected={inputType === CONSTANTS.INPUT_DISTRIBUTION.INPUTS.PREMIUMS}
                    onClick={() => history.push(`${pathname}?variant=premiums`)}
                    data-tip="View distribution of premiums earned"
                    data-for="input-distributions-tooltip"
                  >
                    <Icon name="DollarsPremium" viewBox="0 0 18 18" color="white" m={{ r: '.5rem' }} />
                    PREMIUMS
                  </Button>
                  {INPUTS[CONSTANTS.INPUT_DISTRIBUTION.INPUTS.EFFECTIVE_DATE].DATASET_KEY in inputData && (
                    <Button
                      buttonStyle="tab"
                      selected={inputType === CONSTANTS.INPUT_DISTRIBUTION.INPUTS.EFFECTIVE_DATE}
                      onClick={() => history.push(`${pathname}?variant=effective-date`)}
                      data-tip="View distribution of effective dates"
                      data-for="input-distributions-tooltip"
                    >
                      <Icon name="CalendarEmpty" viewBox="0 0 19 15" color="white" m={{ r: '.5em' }} />
                      EFFECTIVE DATES
                    </Button>
                  )}
                </Col>
                <Col flexGrow="1" d="flex" justify="right" align="center" gutterWidth="0" data-test="filter-buttons">
                  <Icon
                    name="Filter"
                    viewBox="0 0 17 16"
                    size="1rem"
                    m={{ x: '.625rem' }}
                    data-tip="View filter"
                    data-for="input-distributions-tooltip"
                  />
                  <Button
                    buttonStyle="tab"
                    selected={filter === CONSTANTS.INPUT_DISTRIBUTION.FILTERS.CLAIMANTS}
                    onClick={() => setFilter(CONSTANTS.INPUT_DISTRIBUTION.FILTERS.CLAIMANTS)}
                    data-tip="View claimants only"
                    data-for="input-distributions-tooltip"
                  >
                    <Icon name="DollarsUmbrella" viewBox="0 0 17 19" color="white" m={{ r: '.5rem' }} />
                    CLAIMANTS
                  </Button>
                  <Button
                    buttonStyle="tab"
                    selected={filter === CONSTANTS.INPUT_DISTRIBUTION.FILTERS.NON_CLAIMANTS}
                    onClick={() => setFilter(CONSTANTS.INPUT_DISTRIBUTION.FILTERS.NON_CLAIMANTS)}
                    data-tip="View non-claimants only"
                    data-for="input-distributions-tooltip"
                  >
                    <Icon name="PersonUmbrella" viewBox="0 0 16 19" color="white" m={{ r: '.5rem' }} />
                    NON-CLAIMANTS
                  </Button>
                  <Button
                    buttonStyle="tab"
                    m={{ r: '0rem' }}
                    selected={filter === CONSTANTS.INPUT_DISTRIBUTION.FILTERS.ALL}
                    onClick={() => setFilter(CONSTANTS.INPUT_DISTRIBUTION.FILTERS.ALL)}
                    data-tip="View all insureds"
                    data-for="input-distributions-tooltip"
                  >
                    ALL
                  </Button>
                </Col>
              </Row>
              <Row
                bg="white"
                // h="calc(100% - 8rem)"
                flexGrow="1"
                rounded="md"
                p={{ x: '1.5rem', y: '1.25rem' }}
                m={{ t: '1rem', x: '1rem' }}
              >
                <charts.Base.Histogram
                  showPalette={false}
                  showPaletteControl={false}
                  options={{
                    grid: {
                      ...styles.Style1.global.grid,
                      ...styles.Style1.Histogram.grid,
                      top: '10%',
                      bottom: '10%',
                    },
                    textStyle: {
                      ...styles.Style1.Histogram.textStyle,
                    },
                    label: {
                      ...styles.Style1.Histogram.label,
                    },
                    legend: {
                      show: false,
                    },
                    backgroundColor: styles.Style1.global.backgroundColor,
                    tooltip: {
                      show: true,
                      backgroundColor: '#555',
                      textColor: '#fff',
                      borderWidth: 0,
                      formatter: ({ data }) =>
                        data &&
                        `
                          <div style='padding:.75rem 1rem;line-height: 1.4;font-size:1rem;color: #fff;'>
                            ${formatNumberV2(data[1])} insureds
                            <br />
                            ${
                              inputConfig.formatTooltip
                                ? inputConfig.formatTooltip(data)
                                : `${inputConfig.HEADER_NAME} between: ${inputConfig.format(
                                    data[2]
                                  )} - ${inputConfig.format(data[0])}`
                            }
                          </div>
                        `,
                    },
                    xAxis: {
                      ...styles.Style1.Histogram.xAxis,
                      name: inputConfig.HEADER_NAME,
                      nameGap: 35,
                      type: xAxisLog ? 'log' : 'category',
                      axisLabel: {
                        ...styles.Style1.Histogram.xAxis.axisLabel,
                        formatter: inputConfig.formatXAxis || inputConfig.format,
                      },
                      nameTextStyle: {
                        ...styles.Style1.Histogram.xAxis.nameTextStyle,
                      },
                    },
                    yAxis: {
                      ...styles.Style1.Histogram.yAxis,
                      type: yAxisLog ? 'log' : 'value',
                      name: 'Number of insureds',
                    },
                    dataset: {
                      source: chartDataset,
                      sourceHeader: false,
                    },
                    series: [
                      {
                        ...styles.Style1.PxHistogram.series,
                        name: 'Score',
                        type: 'bar',
                        color: chartColor.hex,
                        itemStyle: {
                          barBorderRadius: [10, 10, 0, 0],
                          opacity: 0.75,
                          color: chartColor.hex,
                        },
                        encode: {
                          x: 0,
                          y: 1,
                        },
                        barMaxWidth: xAxisLog ? '8' : null,
                        barCategoryGap: xAxisLog ? '80%' : '15%',
                        animationEasing: 'quinticInOut',
                      },
                    ],
                  }}
                />
              </Row>
              <Row data-debug="spacer2" m={{ x: '0rem' }} p={{ x: '2rem' }}>
                <Col
                  size={12}
                  gutterWidth="0"
                  data-debug="left-toggles-col"
                  borderColor="shadeE6"
                  border={{ t: '1px solid' }}
                />
              </Row>
              <Row data-debug="toggle-row" p={{ t: '1rem', b: '1.5rem' }} data-test="toggle-row">
                <Col
                  size={4}
                  data-debug="left-toggle-col"
                  gutterWidth="6rem"
                  d="flex"
                  justify="left"
                  align="center"
                  flexWrap="nowrap"
                >
                  <Text
                    textSize="subtitle"
                    h="1.125rem"
                    textColor="shade66"
                    style={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}
                    data-tip="Convert Y-axis to log scale"
                    data-for="input-distributions-tooltip"
                  >
                    Y-axis log:
                  </Text>
                  <Text
                    h="1.125rem"
                    m={{ l: '0.5rem' }}
                    textSize="subtitle"
                    textColor={yAxisLog ? 'shade99' : 'shade66'}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    Off
                  </Text>
                  <Div disabled alignSelf="center" m={{ x: '1rem' }} onClick={() => setYAxisLog(!yAxisLog)}>
                    <Switch
                      m="0"
                      checked={yAxisLog}
                      activeColor={yAxisLog ? chartColor.key : 'shade99'}
                      inactiveColor={yAxisLog ? chartColor.key : 'shade99'}
                      activeShadow="0"
                      inactiveShadow="0"
                    />
                  </Div>
                  <Text
                    h="1.125rem"
                    textSize="subtitle"
                    textColor={yAxisLog ? chartColor.key : 'shade99'}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    On
                  </Text>
                </Col>
                <Col
                  size={4}
                  data-debug="center-toggles-col"
                  gutterWidth="0"
                  d="flex"
                  justify="center"
                  align="center"
                  flexWrap="nowrap"
                >
                  <Text
                    textSize="subtitle"
                    h="1.125rem"
                    textColor="shade66"
                    style={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}
                    data-tip="Convert X-axis to log scale"
                    data-for="input-distributions-tooltip"
                  >
                    X-axis log:
                  </Text>
                  <Text
                    h="1.125rem"
                    m={{ l: '0.5rem' }}
                    textSize="subtitle"
                    textColor={!xAxisLog ? 'shade66' : 'shade99'}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    Off
                  </Text>
                  <Div alignSelf="center" m={{ x: '1rem' }} onClick={() => setXAxisLog(!xAxisLog)}>
                    <Switch
                      m="0"
                      checked={xAxisLog}
                      activeColor={xAxisLog ? chartColor.key : 'shade99'}
                      inactiveColor={xAxisLog ? chartColor.key : 'shade99'}
                      activeShadow="0"
                      inactiveShadow="0"
                    />
                  </Div>
                  <Text
                    textSize="subtitle"
                    h="1.125rem"
                    textColor={xAxisLog ? chartColor.key : 'shade99'}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    On
                  </Text>
                </Col>
              </Row>
            </>
          ) : null}
        </ApiWrapper>
      </Div>
    </Div>
  );
}

const mapStateToProps = ({ app, bc, modelView: { postMatchInputDistUrl, preMatchInputDistUrl } }) => ({
  app,
  bc,
  preInputDistDownloadUrl: preMatchInputDistUrl,
  postInputDistDownloadUrl: postMatchInputDistUrl,
});

const mapDispatchToProps = (dispatch) => ({
  setModelName: (modelName) => dispatch(bcActions.setModelName(modelName)),
  fetchPostMatchInputDist: (entityId, entityType) =>
    dispatch(modelViewActions.fetchInputDistDownloadUrl(entityId, entityType)),
  resetPage: () => dispatch(modelViewActions.resetPage()),
});

InputDistributionsModels.propTypes = {
  app: PropTypes.shape({
    profile: PropTypes.shape({
      email: PropTypes.string.isRequired,
    }),
    tenant: PropTypes.string.isRequired,
  }).isRequired,
  setModelName: PropTypes.func.isRequired,
  fetchPostMatchInputDist: PropTypes.func.isRequired,
  postInputDistDownloadUrl: PropTypes.oneOf([undefined, null, PropTypes.string]).isRequired,
  resetPage: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(Page(InputDistributionsModels));
